import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  JobBreakUpDetail,
  JobOrder,
  TripContainer,
} from "../../interfaces/job.interface";
import { containerSizeStringToNumber } from "../../utils/general";
import * as Yup from "yup";
import { useFormik } from "formik";
import "./JobBreakupTable.scss";
import JobService from "../../services/JobService";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../Toaster";
import * as XLSX from "xlsx";
import Modal from "../Modal/Modal";
import { Container } from "../../interfaces/container.interface";
import ContainerType from "../ContainerType/ContainerType";
import TableComponent from "../TableComponent/TableComponent";
import { TableColumn } from "../TableComponent/TableComponent";

interface JobBreakupTableProps {
  job: JobOrder;
  onSave: (data: JobOrder) => void;
  onCancel: () => void;
  excelImportedData?: Container[];
}

export interface JobBreakupTableRef {
  handleSave: () => Promise<void>;
}

// Validation schema for container numbers
const validationSchema = Yup.array().of(
  Yup.array().of(
    Yup.object().shape({
      containerNumber: Yup.string()
        .matches(/^[A-Z]{4}\d{7}$/, "Invalid Container Number")
        .max(11, "Cannot exceed 11 characters")
        .nullable(),
    })
  )
);

const JobBreakupTable = forwardRef<JobBreakupTableRef, JobBreakupTableProps>(
  ({ job, onSave, onCancel, excelImportedData }, ref) => {
    // State for tracking original and edited data separately
    const [originalJobBreakupDetails, setOriginalJobBreakupDetails] = useState<
      JobBreakUpDetail[][]
    >(job.jobBreakups.map((b) => b.jobBreakUpDetails));
    const [editedJobBreakupDetails, setEditedJobBreakupDetails] = useState<
      JobBreakUpDetail[][]
    >(JSON.parse(JSON.stringify(originalJobBreakupDetails))); // Deep copy to avoid mutation
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [uploadedData, setUploadedData] = useState<any[]>([]);
    const [uploadedJobCode, setUploadedJobCode] = useState("");
    const loader = useLoading();

    // Formik setup
    const formik = useFormik({
      initialValues: editedJobBreakupDetails,
      validationSchema,
      onSubmit: () => {
        onSave(job); // Save only if form is valid
      },
      validateOnChange: false,
      validateOnBlur: true,
    });

    // Stats calculation function
    const calculateStats = (details: JobBreakUpDetail[][]) => {
      const totalContainers = details.flat().length;
      const assignedContainers = details
        .flat()
        .filter((detail) => detail.containerNumber).length;
      const pendingContainers = totalContainers - assignedContainers;

      return {
        totalContainers,
        assignedContainers,
        pendingContainers,
      };
    };
    const [stats, setStats] = useState(
      calculateStats(originalJobBreakupDetails)
    );

    const handleCloseModal = () => {
      setIsModalOpen(false);
      setUploadedData([]);
      setUploadedJobCode("");
    };

    const integrateUploadedData = () => {
      setUploadedData([...uploadedData]);
      processUploadedData();
      showToast("Data integrated successfully!", "success");
      handleCloseModal();
    };

    const isIntegrateButtonEnabled = () => {
      return (
        uploadedJobCode.trim() !== "" &&
        uploadedJobCode === job.jobCode.trim().replace(/#/g, "").toUpperCase()
      );
    };

    const processUploadedData = () => {
      if (uploadedData.length === 0) return;

      // Track used container numbers to ensure uniqueness
      const usedContainerNumbers = new Set<string>();

      // Populate usedContainerNumbers with already assigned container numbers
      editedJobBreakupDetails.forEach((breakupDetails) =>
        breakupDetails.forEach((detail) => {
          if (detail.containerNumber) {
            usedContainerNumbers.add(detail.containerNumber);
          }
        })
      );

      // Create updated details by mapping over `editedJobBreakupDetails`
      const updatedDetails = editedJobBreakupDetails.map((breakupDetails) =>
        breakupDetails.map((detail) => {
          // Skip updating if container number is already assigned
          if (detail.containerNumber) return detail;

          // Find an unassigned matching container in `uploadedData`
          const match = uploadedData.find((data) => {
            return (
              data.containerSize ===
                containerSizeStringToNumber(detail.containerSize) &&
              data.containerType === detail.containerType &&
              !usedContainerNumbers.has(data.containerNumber)
            );
          });

          // If a match is found, set container number and mark it as used
          if (match) {
            usedContainerNumbers.add(match.containerNumber);
            return { ...detail, containerNumber: match.containerNumber };
          }

          return detail; // Return original detail if no match found
        })
      );

      // Check if `updatedDetails` is different from `editedJobBreakupDetails` to avoid unnecessary updates
      const isSame =
        JSON.stringify(editedJobBreakupDetails) ===
        JSON.stringify(updatedDetails);

      if (!isSame) {
        setEditedJobBreakupDetails(updatedDetails);
        formik.setValues(updatedDetails); // Update Formik values if details changed
      }
    };

    // Update the edited details on container number change
    const handleContainerNumberChange = (
      jobBreakupIndex: number,
      detailIndex: number,
      value: string
    ) => {
      const formattedValue = value.toUpperCase();
      const updatedDetails = [...editedJobBreakupDetails];
      updatedDetails[jobBreakupIndex][detailIndex].containerNumber =
        formattedValue;

      // Update Formik's state
      setEditedJobBreakupDetails(updatedDetails);
      formik.setFieldValue(
        `${jobBreakupIndex}[${detailIndex}].containerNumber`,
        formattedValue
      );

      // Revalidate the specific field and clear error if valid
      if (/^[A-Z]{4}\d{7}$/.test(formattedValue)) {
        formik.setFieldError(
          `${jobBreakupIndex}[${detailIndex}].containerNumber`,
          ""
        );
      }
    };

    function generateTripContainerList(): TripContainer[] {
      return editedJobBreakupDetails.flatMap((breakupDetails, breakupIndex) =>
        breakupDetails
          .filter(
            (detail, detailIndex) =>
              detail.containerNumber &&
              !originalJobBreakupDetails[breakupIndex][detailIndex]
                .containerNumber
          )
          .map((detail) => ({
            tripId: detail.jobBreakupDetailId,
            containerNumber: detail.containerNumber as string,
          }))
      );
    }

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        const fileType = file.name.split(".").pop()?.toLowerCase();

        if (fileType === "xlsx" || fileType === "xls") {
          // Handle Excel file upload
          const reader = new FileReader();
          reader.onload = (e) => {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json<any>(worksheet, {
              header: 1,
            });

            const parsedJobCode = rows[1]?.[0]
              ?.toString()
              .trim()
              .replace(/#/g, "")
              .toUpperCase(); // Remove any '#' characters
            console.log("Parsed Job Code:", parsedJobCode);
            // Job Code in the first column (index 0)
            console.log("Parsed Job Code:", parsedJobCode);

            setUploadedJobCode(parsedJobCode);
            console.log("Uploaded Job Code ", uploadedJobCode);

            const parsedData = rows.slice(1).map((row: any) => ({
              //jobCode: row[0]?.toString().trim(), // Job Code from the first column
              containerNumber: row[1]?.toString().trim(), // Container Number from the second column
              containerSize: parseInt(row[2], 10), // Size from the third column
              containerType: row[3]?.toString().replace(/\r/g, "").trim(), // Type from the fourth column
            }));

            setUploadedData(parsedData);
            console.log(parsedData);
          };
          reader.readAsArrayBuffer(file);
        } else if (fileType === "csv") {
          // Handle CSV file upload
          const reader = new FileReader();
          reader.onload = (e) => {
            const text = e.target?.result as string;
            const rows = text.split("\n").map((row) => row.split(","));

            const parsedJobCode = rows[1]?.[0]
              ?.toString()
              .trim()
              .replace(/#/g, "")
              .toUpperCase(); // Remove any '#' characters
            setUploadedJobCode(parsedJobCode);
            console.log("Uploaded Job Code ", uploadedJobCode);

            const parsedData = rows.slice(1).map((row: any) => ({
              //jobCode: row[0]?.toString().trim(), // Job Code from the first column
              containerNumber: row[1]?.toString().trim(), // Container Number from the second column
              containerSize: parseInt(row[2], 10), // Size from the third column
              containerType: row[3]?.toString().replace(/\r/g, "").trim(), // Type from the fourth column
            }));

            setUploadedData(parsedData);
            console.log(parsedData);
          };
          reader.readAsText(file);
        } else {
          showToast(
            "Unsupported file format. Please upload an Excel or CSV file.",
            "error"
          );
        }

        // setIsModalOpen(true);

        // // Clear file input
        // event.target.value = "";
      }
    };

    useEffect(() => {
      console.log("Uploaded Job Code:", uploadedJobCode);
    }, [uploadedJobCode]);

    useEffect(() => {
      if (uploadedJobCode) {
        if (uploadedJobCode === job.jobCode) {
          showToast("Job Code matches successfully!", "success");
        } else {
          showToast(
            `Job Code mismatch! Uploaded: ${uploadedJobCode}, Expected: ${job.jobCode}`,
            "error"
          );
        }
      }
    }, [uploadedJobCode, job.jobCode]);

    const handleSave = async () => {
      const errors = await formik.validateForm();

      if (Object.keys(errors).length === 0) {
        const tripList = generateTripContainerList();

        if (tripList.length > 0) {
          try {
            loader.setLoading(true);
            const res = await JobService.assignContainers(tripList);
            const updatedJob = res.data.data;
            showToast(
              `Successfully assigned ${tripList.length} container(s) to Job ${job.jobCode}`,
              "success"
            );
            onSave(updatedJob);
            const updatedStats = calculateStats(editedJobBreakupDetails);
            setStats(updatedStats); // Update stats state
            // setOriginalJobBreakupDetails(editedJobBreakupDetails);
            closeModal();
          } catch (error) {
            console.error("Error assigning containers", error);
            showToast(
              `Failed to assign the containers, please try again !`,
              "error"
            );
          } finally {
            setTimeout(() => {
              loader.setLoading(false);
            }, 1000);
          }
        } else {
          showToast(
            `No new containers to assign. All containers were already assigned.`,
            "info"
          );
          closeModal();
        }
      } else {
        console.log("Invalid form", errors);
        showToast(`Please provide valid container numbers !`, "error");
      }
    };

    const closeModal = () => {
      onCancel();
    };

    useEffect(() => {
      console.log("excelImportedData");
      console.log(excelImportedData);
      if (!excelImportedData) return;
      if (excelImportedData.length === 0) return;
      let count = 0;

      // Track used container numbers to ensure uniqueness
      const usedContainerNumbers = new Set<string>();

      // Populate usedContainerNumbers with already assigned container numbers
      editedJobBreakupDetails.forEach((breakupDetails) =>
        breakupDetails.forEach((detail) => {
          if (detail.containerNumber) {
            usedContainerNumbers.add(detail.containerNumber);
          }
        })
      );

      // Create updated details by mapping over `editedJobBreakupDetails`
      const updatedDetails = editedJobBreakupDetails.map((breakupDetails) =>
        breakupDetails.map((detail) => {
          // console.log(detail);
          // Skip updating if container number is already assigned
          if (detail.containerNumber) return detail;

          // Find an unassigned matching container in `excelImportedData`
          const match = excelImportedData.find((data: any) => {
            return (
              data.containerSize ===
                containerSizeStringToNumber(detail.containerSize) &&
              data.containerType.toUpperCase() === detail.containerType &&
              !usedContainerNumbers.has(data.containerNumber.toUpperCase())
            );
          });

          // If a match is found, set container number and mark it as used
          if (match) {
            count++;
            usedContainerNumbers.add(match.containerNumber.toUpperCase());
            return {
              ...detail,
              containerNumber: match.containerNumber.toUpperCase(),
            };
          }

          return detail; // Return original detail if no match found
        })
      );

      // Check if `updatedDetails` is different from `editedJobBreakupDetails` to avoid unnecessary updates
      const isSame =
        JSON.stringify(editedJobBreakupDetails) ===
        JSON.stringify(updatedDetails);

      if (!isSame) {
        setEditedJobBreakupDetails(updatedDetails);
        formik.setValues(updatedDetails); // Update Formik values if details changed
      }

      if (count > 0) {
        showToast(`Imported ${count} container numbers`, "success");
      } else {
        showToast(`No container numbers were imported`, "warning");
      }
    }, [excelImportedData]);

    // Transform the data for the table
    const tableData = editedJobBreakupDetails.flatMap(
      (breakupDetails, breakupIndex) => {
        const offset = job.jobBreakups
          .slice(0, breakupIndex)
          .reduce(
            (total, current) => total + current.jobBreakUpDetails.length,
            0
          );

        return breakupDetails.map((detail, detailIndex) => ({
          slNo: offset + detailIndex + 1,
          containerNumber: detail.containerNumber || "",
          containerSize: job.jobBreakups[breakupIndex].containerSize,
          containerType: job.jobBreakups[breakupIndex].containerType,
          sizeAndType: `${containerSizeStringToNumber(
            job.jobBreakups[breakupIndex].containerSize
          )} ${job.jobBreakups[breakupIndex].containerType}`,
          transporterName: detail.transporterIdName?.name || "N/A",
          breakupIndex,
          detailIndex,
          isOriginal:
            !!originalJobBreakupDetails[breakupIndex][detailIndex]
              .containerNumber,
          validationError:
            (formik.errors &&
              (formik.errors[breakupIndex] as any)?.[detailIndex]
                ?.containerNumber) ||
            "",
        }));
      }
    );

    const columns: TableColumn[] = [
      {
        header: "Sl No.",
        accessor: "slNo",
        sortable: false,
        centerAlign: true,
        width: "60px",
      },
      {
        header: "Container Number",
        accessor: "containerNumber",
        sortable: false,
        width: "150px",
        cell: (value: any, row: any) => (
          <div className="flex flex-col">
            <input
              maxLength={11}
              type="search"
              value={value || ""}
              onChange={(e) =>
                handleContainerNumberChange(
                  row.breakupIndex,
                  row.detailIndex,
                  e.target.value
                )
              }
              placeholder="Enter Container Number"
              disabled={row.isOriginal}
              className={`container-number-input p-2 border rounded-md ${
                row.validationError ? "border-red-500" : "border-gray-300"
              }`}
            />
            {row.validationError && (
              <div className="text-red-500 text-xs mt-1">
                {row.validationError}
              </div>
            )}
          </div>
        ),
      },
      {
        header: "Size & Type",
        accessor: "sizeAndType",
        sortable: false,
        centerAlign: true,
        width: "100px",
        cell: (value: any) => {
          const [size, type] = value.split(" ");
          const numericSize = containerSizeStringToNumber(size) || 0;
          return <ContainerType size={numericSize} type={type} />;
        },
      },
      {
        header: "Transporter Name",
        accessor: "transporterName",
        sortable: false,
        width: "150px",
      },
    ];

    useImperativeHandle(ref, () => ({
      handleSave: handleSave,
    }));

    return (
      <div className="job-breakup">
        <div className="breakup-table-container">
          <TableComponent
            columns={columns}
            data={tableData}
            showCheckboxes={false}
            showSerialNumbers={false}
            showPagination={false}
            className="containers-table"
          />
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={`Import via Excel for #${job.jobCode}`}
          className="component_modal_container"
        >
          <div className="import_excel_component">
            <div className="upload_area">
              <input
                type="file"
                onChange={handleFileUpload}
                accept=".xlsx, .xls, .csv"
              />
            </div>

            {uploadedJobCode && (
              <div className="flex justify-center p-[10px]">
                <p
                  className={`text-sm font-medium ${
                    uploadedJobCode === job.jobCode
                      ? "text-green-600"
                      : "text-red-500"
                  }`}
                >
                  {uploadedJobCode === job.jobCode ? "✔ " : "✘ "}#
                  {uploadedJobCode}
                </p>
              </div>
            )}

            {/* Data Table with Scrolling */}
            {uploadedData.length > 0 ? (
              <div className="overflow-auto max-h-80 border rounded-lg shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      >
                        Sl. No.
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      >
                        Container Number
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      >
                        Size
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      >
                        Type
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {uploadedData.map((data: any, index: any) => (
                      <tr key={index}>
                        <td className="px-6 py-4 text-sm text-gray-700 text-center">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {data.containerNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {data.containerSize}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {data.containerType}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[11px] text-gray-400 mt-2">
                Please upload an Excel file (.xlsx) to import container data
              </p>
            )}

            {/* Action Buttons */}
            <div className="w-full flex justify-center mt-[20px] gap-[20px]">
              <button
                type="button"
                onClick={handleCloseModal}
                className="secondary_button w-[128px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={integrateUploadedData}
                disabled={!isIntegrateButtonEnabled()}
                className="primary_button  w-[128px]"
              >
                Import
              </button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
);

export default JobBreakupTable;
